import React, { memo, useState, useMemo, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { useIntl, FormattedMessage } from 'react-intl';
import VisibilitySensor from 'react-visibility-sensor';
import debounce from 'lodash.debounce';
import uniqBy from 'lodash.uniqby';

import ModalSelectSearchInput from 'components/ModalSelectSearchInput';
import Overlay from 'components/Overlay';
import ModalSelectHeader from 'components/ModalSelectHeader';
import Spinner from 'components/Spinner';
import { ParagraphSmall } from 'components/Typography';

import { getLocaleInDBFormat, request, filterExcludedCurrencies } from 'utils';

import {
  Wrapper,
  VisibilitySensorWrapper,
  CheckboxWrapper,
  CurrencyCheckbox,
  CurrencyName,
  SaveButton,
  CurrenciesList,
  SpinnerWrapper,
  CurrencyFlag,
  NoDataLabel,
  OptionsTitle,
} from './styles';

const CurrenciesSelect = memo(
  ({
    currencies,
    selectedCurrencies,
    onRequestReset,
    loading,
    sourceCurrency,
    targetCurrency,
    onRequestChange,
    onScrollEnd,
  }) => {
    const { formatMessage, locale } = useIntl();

    const [selectedCurrenciesSet, changeSelectedCurrenciesSet] = useState(
      new Map(selectedCurrencies.map(item => [item.code, item]))
    );
    const [isFocused, changeFocus] = useState(false);
    const [changeInitialized, initChange] = useState(false);
    const [searchValue, changeSearchValue] = useState('');
    const [searchInitialized, initSearch] = useState(false);
    const [searchItems, changeSearchItems] = useState([]);
    const [searchLoading, changeSearchLoading] = useState(false);
    const [searchCount, changeSearchCount] = useState(0);

    const visibilitySensorWrapperRef = useRef();
    const searchRef = useRef();
    const headerRef = useRef();

    const headerTitle = useMemo(() => formatMessage({ id: 'addCurrency' }), []);
    const closeButtonTitle = useMemo(() => formatMessage({ id: 'close' }), []);
    const inputPlaceholder = useMemo(
      () =>
        formatMessage({
          id: `currencyDropdown.searchInput.placeholder`,
        }),
      []
    );
    const cancelButtonLabel = useMemo(
      () =>
        formatMessage({
          id: 'currencyDropdown.searchInput.cancelButtonLabel',
        }),
      []
    );
    const saveLabel = useMemo(() => formatMessage({ id: 'save' }), []);

    const onFocus = useCallback(() => changeFocus(true), []);
    const onBlur = useCallback(() => changeFocus(false), []);

    const search = useCallback(
      async (value, isPaginationFetch = false) => {
        const shouldFetch = searchItems.length < searchCount;

        try {
          if (shouldFetch || !isPaginationFetch) {
            changeSearchLoading(true);

            const start = isPaginationFetch ? searchItems.length : 0;

            const requestUrl = `${
              process.env.APP_CURRENCIES_API_URL
            }/?locale=${getLocaleInDBFormat(
              locale
            )}&type=money&start=${start}&cpp=${15}&search=${value}`;

            const response = await request({
              url: requestUrl,
            });

            initSearch(true);
            changeSearchLoading(false);
            changeSearchCount(response.pagination.count);
            changeSearchItems(
              isPaginationFetch
                ? [
                    ...filterExcludedCurrencies(searchItems),
                    ...filterExcludedCurrencies(response.list),
                  ]
                : filterExcludedCurrencies(response.list)
            );
          }
        } catch (error) {
          changeSearchLoading(false);
        }
      },
      [searchItems, searchCount]
    );

    const onSearch = useMemo(() => debounce(search, 500), [search]);

    const onSearchInputChange = useCallback(event => {
      const { value } = event.target;

      changeSearchValue(value);
      onSearch(value);
    });

    const onCancel = useCallback(event => {
      event.stopPropagation();

      changeFocus(false);
      changeSearchValue('');
    }, []);

    const onDropdownScroll = useCallback(() => {
      if (visibilitySensorWrapperRef.current.scrollTop > 0) {
        if (searchRef.current && isFocused && searchValue) {
          searchRef.current.style.boxShadow =
            '0px 4px 24px rgba(0, 0, 0, 0.14)';
        }

        if (headerRef.current) {
          headerRef.current.style.boxShadow =
            '0px 4px 24px rgba(0, 0, 0, 0.14)';
        }
      }

      if (visibilitySensorWrapperRef.current.scrollTop === 0) {
        if (searchRef.current) {
          searchRef.current.style.boxShadow = '';
        }
        if (headerRef.current) {
          headerRef.current.style.boxShadow = '';
        }
      }
    }, [
      isFocused,
      searchValue,
      visibilitySensorWrapperRef.current,
      searchRef.current,
      headerRef.current,
    ]);

    const onCurrencyCheckboxChange = useCallback(
      event => {
        const currencyCode = event.target.value;

        const newSelectedCurrenciesSet = new Map(selectedCurrenciesSet);

        if (selectedCurrenciesSet.has(currencyCode)) {
          newSelectedCurrenciesSet.delete(currencyCode);
        } else {
          const currencyItem = [...selectedCurrencies, ...currencies].find(
            ({ code }) => code === currencyCode
          );
          newSelectedCurrenciesSet.set(currencyCode, currencyItem);
        }

        initChange(true);
        changeSelectedCurrenciesSet(newSelectedCurrenciesSet);
      },
      [selectedCurrenciesSet, selectedCurrencies, currencies]
    );

    const onSaveBtnClick = useCallback(() => {
      const allCurrencies = [...currencies, ...searchItems];
      const selectedCurrenciesInfo = uniqBy(
        allCurrencies,
        'code'
      ).filter(({ code }) => selectedCurrenciesSet.has(code));

      onRequestChange(
        Array.from(selectedCurrenciesSet.values()),
        selectedCurrenciesInfo
      );
    }, [onRequestChange, currencies, searchItems, selectedCurrenciesSet]);

    const onChangeVisibility = useCallback(
      isVisible => {
        if (isVisible) {
          if (searchValue) {
            search(searchValue, true);
          } else {
            onScrollEnd();
          }
        }
      },
      [searchValue, onScrollEnd, search]
    );

    const getBaseCurrency = useCallback(
      code =>
        code.toLowerCase() === targetCurrency ||
        code.toLowerCase() === sourceCurrency,
      [sourceCurrency, targetCurrency]
    );

    const favoritesCurrencies = useMemo(
      () =>
        filterExcludedCurrencies(
          Array.from(selectedCurrenciesSet.values()).filter(
            ({ code }) => !getBaseCurrency(code)
          )
        ),
      [getBaseCurrency, selectedCurrenciesSet]
    );

    const currenciesList = useMemo(
      () =>
        filterExcludedCurrencies(
          (searchValue ? searchItems : currencies).filter(
            ({ code }) => !getBaseCurrency(code)
          )
        ),
      [getBaseCurrency, currencies, searchItems, searchValue]
    );

    return (
      <Wrapper>
        <ModalSelectHeader
          ref={headerRef}
          title={headerTitle}
          closeTitle={closeButtonTitle}
          onClose={onRequestReset}
        >
          <ModalSelectSearchInput
            type="text"
            ref={searchRef}
            isFocused={isFocused}
            value={searchValue}
            label={inputPlaceholder}
            placeholder={inputPlaceholder}
            cancelLabel={cancelButtonLabel}
            onChange={onSearchInputChange}
            onFocus={onFocus}
            onCancel={onCancel}
          />
        </ModalSelectHeader>

        <VisibilitySensorWrapper
          ref={visibilitySensorWrapperRef}
          onScroll={onDropdownScroll}
        >
          <VisibilitySensor
            partialVisibility="bottom"
            containment={visibilitySensorWrapperRef.current}
            onChange={onChangeVisibility}
          >
            <CurrenciesList isFocused={isFocused}>
              {!searchValue && favoritesCurrencies.length > 0 && (
                <OptionsTitle>
                  <FormattedMessage id="favorites" />
                </OptionsTitle>
              )}

              {!searchValue &&
                favoritesCurrencies.map(({ name, code }) => (
                  <CheckboxWrapper key={code}>
                    <CurrencyCheckbox
                      value={code}
                      checked
                      onChange={onCurrencyCheckboxChange}
                      checkPosition="start"
                    >
                      <CurrencyFlag name={code} />
                      {code}
                      <CurrencyName>{name}</CurrencyName>
                    </CurrencyCheckbox>
                  </CheckboxWrapper>
                ))}

              {!searchValue && (
                <OptionsTitle>
                  <FormattedMessage id="currenciesType.money.long" />
                </OptionsTitle>
              )}

              {currenciesList.map(({ name, code }) => (
                <CheckboxWrapper key={code}>
                  <CurrencyCheckbox
                    value={code}
                    checked={selectedCurrenciesSet.has(code)}
                    onChange={onCurrencyCheckboxChange}
                    checkPosition="start"
                  >
                    <CurrencyFlag name={code} />
                    {code}
                    <CurrencyName>{name}</CurrencyName>
                  </CurrencyCheckbox>
                </CheckboxWrapper>
              ))}

              {!!searchValue &&
                searchItems.length === 0 &&
                !loading &&
                !searchLoading &&
                searchInitialized && (
                  <NoDataLabel>
                    <ParagraphSmall>
                      <FormattedMessage id="noData.noFound" />
                    </ParagraphSmall>
                    <ParagraphSmall>
                      <FormattedMessage id="noData.tryAnother" />
                    </ParagraphSmall>
                  </NoDataLabel>
                )}
            </CurrenciesList>
          </VisibilitySensor>

          {(loading || searchLoading) && (
            <SpinnerWrapper>
              <Spinner />
            </SpinnerWrapper>
          )}
        </VisibilitySensorWrapper>

        <Overlay
          show={!searchValue.trim() && isFocused}
          offset={72}
          onClick={onBlur}
        />

        {changeInitialized && (
          <SaveButton type="button" onClick={onSaveBtnClick} title={saveLabel}>
            {saveLabel}
          </SaveButton>
        )}
      </Wrapper>
    );
  }
);

CurrenciesSelect.propTypes = {
  currencies: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      code: PropTypes.string.isRequired,
    })
  ).isRequired,
  selectedCurrencies: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      code: PropTypes.string.isRequired,
    })
  ).isRequired,
  onRequestReset: PropTypes.func.isRequired,
  onRequestChange: PropTypes.func.isRequired,
  onScrollEnd: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  sourceCurrency: PropTypes.string.isRequired,
  targetCurrency: PropTypes.string.isRequired,
};

export default CurrenciesSelect;
